import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import axios from "axios";
import { usePagination } from "hooks";
import distributorsRequest from "axios/distributorsRequest";
import { DISTRIBUTORS_CALCULATION, DISTRIBUTORS_CODES } from "constants/api";
import { DATE_QUERY_FORMAT } from "constants/dateFormats";
import { DISTRIBUTOR_TYPE } from "./constants";

const distributorsDataMapper = (data, intl) =>
  data.operations.map((item) => ({
    key: `${item.user_id}${item.distributor_id}`,
    referal: {
      outcost: item.outcost_count,
      name: item.name,
    },
    operations: {
      distributorCode: item.distributor_code,
      inCostCount: item.incost_count,
    },
    transacted: item.incost_total,
    type: item.distributor_type,
    commisions: item.incost_commission,
    cost: item.incost_cost,
    net: item.incost_profit,
    distributor: item.distributor,
    expandableRow: {
      username: item.username,
      phone: item.phone || "-",
      applyDate: intl.formatDate(item.apply, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        timeZone: "UTC",
      }),
      referals: item.distributor_childs_count,
      distributorCode: item.distributor_code,
      calculated: {
        operations: item.incost_count,
        transacted: item.incost_total,
        commisions: item.incost_commission,
        cost: item.incost_cost,
        net: item.incost_profit,
        distributor: item.distributor,
      },
      notCalculated: {
        operations: item.outcost_count,
        transacted: item.outcost_total,
        commisions: item.outcost_commission,
        cost: item.outcost_cost,
        net: intl.formatMessage({ id: "distributors.noData" }),
        distributor: intl.formatMessage({ id: "distributors.noData" }),
      },
      total: {
        operations: item.transaction_count,
        transacted: item.transaction_total,
        commisions: item.transaction_commission,
        cost: item.transaction_cost,
        net: item.incost_profit,
        distributor: item.distributor,
      },
    },
  }));

const getDistributorType = (typeNumber) => {
  if (typeNumber === 2) {
    return DISTRIBUTOR_TYPE.master;
  }

  if (typeNumber === 1) {
    return DISTRIBUTOR_TYPE.sub;
  }

  // if typeNumber != 1 || 2 we assume is sub distributor because it shows less info
  return DISTRIBUTOR_TYPE.sub;
};

const useDistributors = (range) => {
  const [loading, setLoading] = useState(true);
  const [distributors, setDistributors] = useState({ rows: [] });
  const [dashboard, setDashborad] = useState({});
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState();
  const [userType, setUserType] = useState();

  const { paginationConfig } = usePagination(distributors?.total);
  const intl = useIntl();

  const onCodeSelectorChange = (evt) => {
    const selectedValue = evt.target.value;
    setCode(selectedValue);
    const selectedItem = codes.find((item) => item.code === selectedValue);
    setUserType(getDistributorType(selectedItem));
  };

  const getUserCode = async () => {
    setLoading(true);

    try {
      const response = await axios.get(DISTRIBUTORS_CODES);

      if (response?.data?.success) {
        const codesData = response.data.result;

        if (codesData?.length) {
          setCode(codesData[0].code);
          setUserType(getDistributorType(codesData[0].type));
          setCodes(codesData);
        }
      }
    } catch (error) {
      console.error("Backend error", error);
    }
  };

  const getDistributorsData = async () => {
    setLoading(true);

    const date = range
      ? {
          startDate: range.startDate.format(DATE_QUERY_FORMAT),
          endDate: range.endDate.format(DATE_QUERY_FORMAT),
        }
      : undefined;

    try {
      const response = await distributorsRequest.get(
        DISTRIBUTORS_CALCULATION.replace("{code}", code),
        {
          params: {
            ...date,
            limit: paginationConfig.limit,
            offset: paginationConfig.offset,
          },
        }
      );

      if (response?.data?.success) {
        const data = response.data.result;

        setDashborad({
          readers: data.resume.readers.calculate,
          transacted: data.resume.summary.transaction_total,
          benefits: data.resume.summary.profit / 100,
          goalPrecentage: data.resume.summary.goal.percentage,
          distributor: data.resume.summary.distributor,
        });

        setDistributors({
          rows: distributorsDataMapper(data, intl),
          total: data.operationsCount,
        });
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        setDashborad({
          readers: 0,
          transacted: 0,
          benefits: 0,
          goalPrecentage: 0,
          srPagoNet: 0,
          distributor: 0,
        });

        setDistributors({
          rows: [],
          total: 0,
        });
      } else {
        console.error("Backend error", error);
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    getUserCode();
  }, []);

  useEffect(() => {
    if (code) {
      getDistributorsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    if (code) {
      getDistributorsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, paginationConfig.limit, paginationConfig.offset]);

  return {
    loading,
    paginationConfig,
    distributors,
    dashboard,
    code,
    onCodeSelectorChange,
    codes,
    userType,
  };
};

export default useDistributors;
